import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { INITIATIVE_STATUSES, SEARCH_TERM_MAX_LENGTH } from '@complan/contracts';
import { Button } from '@/components/ui/button';
import { STATUS_LABEL_KEYS } from '@/shared/i18n/enum-labels';
import {
  LIST_PAGE_SIZES,
  type InitiativeStatus,
} from '@/shared/url/initiative-list-search-params';
import type { PlatformOption } from './initiative-list-data';

/**
 * The Initiative list's filter bar (UI spec 3.6): a white strip above the content,
 * 1px border, `9px 12px` padding, flex-wrapping with a 9px gap, each control a
 * 10.5px uppercase muted micro-label above a padded input of `min-width: 112px`,
 * and a right-aligned action cluster.
 *
 * Three dimensions, matching the click dummy's own list filters: Status, Platform
 * and a free-text Search. The bar holds no filter state - every control reports
 * upward and the page writes the URL, so the table, the result count and the page
 * window all read from one source (UI spec 3.6's "one filter bar drives everything
 * on the page").
 *
 * The page-size control lives here rather than beside the pager because it is a
 * property of the view, like a filter, and the spec puts view controls in this
 * strip. It offers exactly the sizes the codec accepts.
 */
export interface InitiativeListFilterBarProps {
  readonly statuses: readonly InitiativeStatus[];
  readonly platformIds: readonly string[];
  readonly search: string | null;
  readonly limit: number;
  readonly platforms: readonly PlatformOption[];
  readonly resultCount: number | null;
  readonly onStatusesChange: (statuses: readonly InitiativeStatus[]) => void;
  readonly onPlatformIdsChange: (platformIds: readonly string[]) => void;
  readonly onSearchChange: (search: string | null) => void;
  readonly onLimitChange: (limit: number) => void;
  readonly onReset: () => void;
}

/** How long the Search box waits after the last keystroke before filtering. */
const SEARCH_DEBOUNCE_MS = 300;

export function InitiativeListFilterBar({
  statuses,
  platformIds,
  search,
  limit,
  platforms,
  resultCount,
  onStatusesChange,
  onPlatformIdsChange,
  onSearchChange,
  onLimitChange,
  onReset,
}: InitiativeListFilterBarProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div>
      <div
        role="search"
        aria-label={t('initiativeList.filterBarLabel')}
        className="border-line bg-panel mb-[14px] flex flex-wrap items-end gap-[9px] rounded-[var(--radius-card)] border px-3 py-[9px]"
      >
        <Control label={t('initiativeList.filterStatus')}>
          {(id) => (
            <SingleSelect
              id={id}
              // One status at a time keeps the control a plain `<select>` with a
              // real accessible name. The dimension itself is multi-value, so the
              // URL and the request stay a list and a multi-select is a drop-in
              // later without a contract change.
              value={statuses[0] ?? ''}
              onChange={(value) => onStatusesChange(value === '' ? [] : [value as InitiativeStatus])}
            >
              <option value="">{t('initiativeList.allStatuses')}</option>
              {INITIATIVE_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {t(STATUS_LABEL_KEYS[status])}
                </option>
              ))}
            </SingleSelect>
          )}
        </Control>

        <Control label={t('initiativeList.filterPlatform')}>
          {(id) => (
            <SingleSelect
              id={id}
              value={platformIds[0] ?? ''}
              onChange={(value) => onPlatformIdsChange(value === '' ? [] : [value])}
            >
              <option value="">{t('initiativeList.allPlatforms')}</option>
              {platforms.map((platform) => (
                <option key={platform.id} value={platform.id}>
                  {platform.isInactive
                    ? t('initiativeList.inactiveOption', { name: platform.label })
                    : platform.label}
                </option>
              ))}
            </SingleSelect>
          )}
        </Control>

        <Control label={t('initiativeList.filterSearch')}>
          {(id) => <SearchInput id={id} value={search} onCommit={onSearchChange} />}
        </Control>

        <Control label={t('initiativeList.pageSize')}>
          {(id) => (
            <SingleSelect
              id={id}
              value={String(limit)}
              onChange={(value) => onLimitChange(Number.parseInt(value, 10))}
            >
              {LIST_PAGE_SIZES.map((size) => (
                <option key={size} value={String(size)}>
                  {size}
                </option>
              ))}
            </SingleSelect>
          )}
        </Control>

        <div className="ml-auto flex items-end gap-2 self-end">
          <Button variant="outline" size="sm" onClick={onReset}>
            {t('common.reset')}
          </Button>
        </div>
      </div>
      {resultCount !== null ? (
        <span className="sr-only" aria-live="polite">
          {t('initiativeList.resultCount', { count: resultCount })}
        </span>
      ) : null}
    </div>
  );
}

/**
 * A single labelled control: the micro-label above the input, with the generated
 * id handed to `children` so the rendered input adopts it and the `<label
 * htmlFor>` names something focusable (R18.6).
 */
function Control({
  label,
  children,
}: {
  readonly label: string;
  readonly children: (id: string) => React.ReactNode;
}): React.JSX.Element {
  const id = useId();
  return (
    <div className="flex min-w-[112px] flex-col gap-1">
      <label
        htmlFor={id}
        className="text-label text-[10.5px] font-semibold uppercase tracking-wide"
      >
        {label}
      </label>
      {children(id)}
    </div>
  );
}

const CONTROL_CLASS =
  'border-line bg-panel text-txt min-w-[112px] rounded-[var(--radius-control)] border px-[7px] py-[5px] text-[12px] outline-none focus-visible:border-accent-line';

function SingleSelect({
  id,
  value,
  onChange,
  children,
}: {
  readonly id: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <select
      id={id}
      className={CONTROL_CLASS}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      {children}
    </select>
  );
}

/**
 * The Search box.
 *
 * Debounced, so typing a term issues one request after a pause rather than one per
 * keystroke, each of which would also push a URL entry. The committed value is
 * reported as `null` when the box is empty, which is what the filter grammar reads
 * as unfiltered - an empty string would otherwise apply a dimension matching every
 * row.
 *
 * The draft is local while typing and re-seeded whenever the committed value
 * changes from outside (a Reset, or the back button), so the box always shows the
 * term the table was filtered by.
 */
function SearchInput({
  id,
  value,
  onCommit,
}: {
  readonly id: string;
  readonly value: string | null;
  readonly onCommit: (value: string | null) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value ?? '');

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  useEffect(() => {
    const trimmed = draft.trim();
    const next = trimmed === '' ? null : trimmed;
    if (next === value) {
      return;
    }
    const timer = setTimeout(() => onCommit(next), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft, value, onCommit]);

  return (
    <input
      id={id}
      type="search"
      className={CONTROL_CLASS}
      value={draft}
      maxLength={SEARCH_TERM_MAX_LENGTH}
      placeholder={t('initiativeList.searchPlaceholder')}
      onChange={(event) => setDraft(event.target.value)}
    />
  );
}
